// Temporary store managers ("temp packers") — pure resolvers shared by the
// driver app, the dispatch order cards and the history writers.
//
// Since migration 0050 a store can have SEVERAL managers, held in the
// `store_temp_managers` table. The old single field on the store user
// (`users.temp_packer_driver_id` / `User.tempPackerDriverId`) is kept as a
// mirror of the most recent assignment so old builds keep working. Readers
// therefore take the UNION of both: a driver is a manager of a store when a
// table row names them OR the store's legacy field names them. In Firestore
// mode the table does not exist (assignments = []), which reduces exactly to
// the old single-slot behaviour.
//
// Mobile keeps a copy in services/tempManagers.ts — keep the two in sync.
import type { StoreTempManager, User } from '../types';

/** Driver ids managing `storeId`, most recent assignment first, legacy last. */
export function tempManagerIdsForStore(
  storeId: string | null | undefined,
  users: readonly User[],
  assignments: readonly StoreTempManager[],
): string[] {
  if (!storeId) return [];
  const ids: string[] = [];
  const rows = assignments
    .filter((a) => a.storeId === storeId)
    .sort((a, b) => (b.assignedAt || 0) - (a.assignedAt || 0) || a.driverId.localeCompare(b.driverId));
  for (const r of rows) if (!ids.includes(r.driverId)) ids.push(r.driverId);
  const legacy = users.find((u) => u.id === storeId)?.tempPackerDriverId;
  if (legacy && !ids.includes(legacy)) ids.push(legacy);
  return ids;
}

export function isTempManagerOf(
  driverId: string | null | undefined,
  storeId: string | null | undefined,
  users: readonly User[],
  assignments: readonly StoreTempManager[],
): boolean {
  if (!driverId || !storeId) return false;
  return tempManagerIdsForStore(storeId, users, assignments).includes(driverId);
}

/** Store users (role 'store') the driver currently manages, in users order. */
export function storesManagedBy(
  driverId: string | null | undefined,
  users: readonly User[],
  assignments: readonly StoreTempManager[],
): User[] {
  if (!driverId) return [];
  const fromTable = new Set(assignments.filter((a) => a.driverId === driverId).map((a) => a.storeId));
  return users.filter(
    (u) => u.role === 'store' && (fromTable.has(u.id) || u.tempPackerDriverId === driverId),
  );
}

// ── After a committed write ─────────────────────────────────────────────────
// The writer applies its own change at once instead of waiting for the live
// feed (1–3 s) and the polled users list (up to 30 s), which used to leave a
// removed manager on screen after the "removed" toast (2026-10-15).

/** The rows with (store, driver) in them — the row an insert just committed. */
export function withManagerAdded(
  assignments: readonly StoreTempManager[],
  row: StoreTempManager,
): StoreTempManager[] {
  return [...assignments.filter((a) => !(a.storeId === row.storeId && a.driverId === row.driverId)), row];
}

/** The rows without the store's removed manager — or every manager of the
 *  store when `driverId` is absent, as the delete did. */
export function withManagersRemoved(
  assignments: readonly StoreTempManager[],
  storeId: string,
  driverId?: string,
): StoreTempManager[] {
  return assignments.filter((a) => !(a.storeId === storeId && (!driverId || a.driverId === driverId)));
}

/** The store's legacy mirror as the 0050 trigger sets it after a change: the
 *  most recently assigned remaining manager, or null when none is left. */
export function legacyMirrorFor(
  storeId: string,
  assignments: readonly StoreTempManager[],
): string | null {
  return tempManagerIdsForStore(storeId, [], assignments)[0] ?? null;
}

/** Row mapper for the `store_temp_managers` table. */
export const sbStoreTempManagerFromRow = (r: any): StoreTempManager => ({
  storeId: r.store_id,
  driverId: r.driver_id,
  assignedBy: r.assigned_by ?? null,
  assignedAt: r.assigned_at ? Date.parse(r.assigned_at) : 0,
});
